import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import { getLocales } from 'assets/locales/Locale';
import Sidebar from 'assets/templates/sidebar/Sidebar';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { first } from 'lodash';
import PaymentType from 'models/enums/PaymentType';
import ClaimJobPaymentItemUsBankPayment from '../../claimJobPaymentItem/sections/other/Finance.ClaimJobPaymentItem.UsBankPayment';
import { useClaimJobPaymentProvider } from '../Finance.ClaimJobPayment.Hooks';

export default function ClaimJobPaymentInfo() {
  const { lang, translate } = getLocales();
  const { selected, pageRouter, reload } = useClaimJobPaymentProvider();

  if (!selected || pageRouter.params?.action !== 'info') return null;

  const paymentMethodCode = selected?.paymentMethodCode ?? first(selected?.claimJobPaymentItems)?.paymentMethodCode;

  return (
    <Sidebar
      onClose={() => pageRouter.updateParams({})}
      icon={<Icon class="if-database" />}
      title={selected.referenceNumber}
      content={
        <>
          <h3>{lang.info}</h3>
          <div>
            {lang.referenceNumber}: <b>{selected.referenceNumber}</b>
          </div>
          <div>
            {lang.claimNumber}: <b>{selected.claimJobPaymentItems[0]?.claimJob?.claim?.number}</b>
          </div>
          <div>
            {lang.owner}: <b>{selected.owner?.fullName ?? 'N/A'}</b>
          </div>
          <div>
            {lang.paymentAt}:{' '}
            <b>{selected.createdAtLocal ? localizeDate(selected?.createdAtLocal, defaultDateTimeFormat) : 'N/A'}</b>
          </div>

          <h3>{lang.dealer}</h3>
          <div>
            {lang.dealerName}: <b>{selected.claimJobPaymentPayee?.dealer?.name ?? 'N/A'}</b>
          </div>
          <div>
            {lang.dealerId}: <b>{selected.claimJobPaymentPayee?.dealer?.id ?? 'N/A'}</b>
          </div>

          <h3>{lang.payee}</h3>
          <div>
            {lang.payeeName}: <b>{selected.claimJobPaymentPayee?.displayInfo?.title}</b>
          </div>
          <div>
            {lang.payeeEmail}:{' '}
            <b>
              {selected.claimJobPaymentPayee?.getEmailForPayment({
                clientOverrideEmail: selected.claimJobPaymentItems[0]?.claimJob?.claim?.contactEmail,
              }) ?? 'N/A'}
            </b>
          </div>
          <div>
            {lang.address}: <b>{selected.claimJobPaymentPayee?.fullAddress ?? 'N/A'}</b>
          </div>
          <div>
            {lang.city}: <b>{selected.claimJobPaymentPayee?.addressDetails?.city ?? 'N/A'}</b>
          </div>
          <div>
            {lang.province}: <b>{selected.claimJobPaymentPayee?.addressDetails?.province ?? 'N/A'}</b>
          </div>
          <div>
            {lang.postalCode}: <b>{selected.claimJobPaymentPayee?.addressDetails?.postalCode ?? 'N/A'}</b>
          </div>

          <h3>{lang.prices}</h3>
          <div>
            {lang.totalBeforeTax}:{' '}
            <b>
              <FormatValue
                value={`${(selected.totalBeforeTax).toFixed(2)}$`}
              />
            </b>
          </div>
          <div>
            {lang.deduction}:{' '}
            <b>
              <FormatValue value={`${(selected.deduction || 0).toFixed(2)}$`} />
            </b>
          </div>
          <div>
            {selected.currency === 'CAD' ? lang.gstHstPaid : lang.tax}:{' '}
            <b>
              <FormatValue value={`${(selected.gstHstAmountPaid || 0).toFixed(2)}$`} />
            </b>
          </div>
          {selected.currency === 'CAD' && (
            <div>
              {lang.pstPaid}:{' '}
              <b>
                <FormatValue value={`${(selected.provincialSalesTaxAmountPaid || 0).toFixed(2)}$`} />
              </b>
            </div>
          )}
          <div>
            {lang.paidAmount}:{' '}
            <b>
              <FormatValue value={`${(selected.amountPaid || 0).toFixed(2)}$`} />
            </b>
          </div>

          <h3>{lang.items}</h3>
          {selected.claimJobPaymentItems.map((item) => (
            <div key={'claimJobPaymentItem_' + item.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.M }}>
                <div>
                  {lang.jobNumber}: <b>{item.jobNumber}</b>
                </div>
                <div>
                  <ClaimJobPaymentItemUsBankPayment payment={selected} paymentItem={item} reload={reload} />
                </div>
              </div>
              {item.isPaymentSentManually && (
                <div>
                  {lang.isPaymentSentManually}: <Icon class="fas-check" />
                </div>
              )}
              {item.isPaymentSentViaUsBank && (
                <div>
                  {lang.isPaymentSentViaUsBank}: <Icon class="fas-check" />
                </div>
              )}
              {item.paymentRequestedAtUtc && (
                <div>
                  {lang.paymentCreated}: <b>{localizeDate(item.paymentRequestedAtUtc, defaultDateTimeFormat)}</b>
                </div>
              )}
              <div>
                {lang.authorizationNumber}: <b>{item.claimJob.authorizationNumber}</b>
              </div>
              <div>
                {lang.itemTotal}:{' '}
                <b>
                  <FormatValue value={`${(item.total || 0).toFixed(2)}$`} />
                </b>
              </div>
              <br />
            </div>
          ))}
          <div>
            {lang.totalItems}: <b>{selected.claimJobPaymentItems?.length ?? 0}</b>
          </div>
          <div>
            {lang.totalPaid}:{' '}
            <b>
              <FormatValue value={`${(selected.amountPaid || 0).toFixed(2)}$`} />
            </b>
          </div>

          <h3>{lang.payment}</h3>
          <div>
            {lang.postingAccount}: <b>{selected.ledgerAccount?.ledgerAccountName ?? 'N/A'}</b>
          </div>
          <div>
            {lang.paymentMethod}: <b>{paymentMethodCode ? translate(paymentMethodCode) : 'N/A'}</b>
          </div>

          <h3>{lang.paymentInfoDetails}</h3>
          <div>
            {lang.paymentUser}: <b>{selected.owner?.fullName ?? 'N/A'}</b>
          </div>
          <div>
            {lang.transactionDate}: <b>{localizeDate(selected.transactionDateUtc, defaultDateTimeFormat) ?? 'N/A'}</b>
          </div>
          {selected.paymentMethodCode === PaymentType.eft && (
            <>
              <div>
                {lang.bankTransit}: <b>{selected.bankTransit ?? 'N/A'}</b>
              </div>
              <div>
                {lang.bankBranch}: <b>{selected.bankBranch ?? 'N/A'}</b>
              </div>
              <div>
                {lang.bankAccount}: <b>{selected.bankAccount ?? 'N/A'}</b>
              </div>
              <div>
                {lang.remittanceEmailAddress}: <b>{selected.remittanceEmailAddress ?? 'N/A'}</b>
              </div>
            </>
          )}
          {selected.paymentMethodCode === PaymentType.cheque && (
            <>
              <div>
                {lang.chequeNumber}: <b>{selected.chequeNumber ?? 'N/A'}</b>
              </div>
              <div>
                {lang.remittanceEmailAddress}: <b>{selected.remittanceEmailAddress ?? 'N/A'}</b>
              </div>
            </>
          )}
          {(selected.paymentMethodCode === PaymentType.ETransfer ||
            selected.paymentMethodCode === PaymentType.usBank) && (
            <>
              {selected.paymentMethodCode === PaymentType.ETransfer && (
                <>
                  <div>
                    {lang.transactionNumber}: <b>{selected.transactionNumber ?? 'N/A'}</b>
                  </div>
                  <div>
                    {lang.eTransferQuestion}: <b>{selected.eTransferQuestion ?? 'N/A'}</b>
                  </div>
                  <div>
                    {lang.eTransferAnswer}: <b>{selected.eTransferAnswer ?? 'N/A'}</b>
                  </div>
                </>
              )}
              <div>
                {lang.paymentEmailAddress}: <b>{selected.paymentEmailAddress ?? 'N/A'}</b>
              </div>
              <div>
                {lang.remittanceEmailAddress}: <b>{selected.remittanceEmailAddress ?? 'N/A'}</b>
              </div>
            </>
          )}
        </>
      }
    />
  );
}
